/**
 * EmailMarketingView — the governed sequence-enrollment surface.
 *
 * The first real UI over src/lib/crm/sequenceEnrollment.ts: pick a cohort
 * (saved views + honest built-ins), pick a sequence, PREVIEW the blast
 * radius (enrolled vs suppressed with per-contact reasons + provenance),
 * then submit the whole thing as ONE vetoed batch of send diffs into the
 * real approvals collection. Nothing here sends email — approving a diff
 * creates a governed send task via the existing executor, which is the
 * honest state of the send path until a credential'd mail atom exists.
 *
 * Every suppression signal is derived from REAL data: the contact's own
 * activity timeline (replies/bounces/unsubscribes), prospect deal stage,
 * live tickets, customer status, unified-timeline human touches, and
 * prior enrollments already sitting in the approvals collection.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  AlignLeft,
  BarChart3,
  Building2,
  CalendarClock,
  CheckCircle2,
  Clock,
  Cpu,
  FileCheck2,
  Globe,
  HeartHandshake,
  Inbox,
  LayoutTemplate,
  Lightbulb,
  Loader2,
  Mail,
  Minus,
  MousePointerClick,
  Newspaper,
  Radar,
  RefreshCw,
  Reply,
  Send,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  Trash2,
  Trophy,
  Type,
  Undo2,
  Users,
  Workflow,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { useOptionalToast } from '../components/ToastProvider';
import { findSequenceByName } from '../lib/crm/sequenceRecipes';
import { C } from '../lib/tokens';
import { bootstrapPeopleIfEmpty, subscribePeople } from '../lib/db/people';
import { listTickets } from '../lib/db/tickets';
import { subscribeSavedViews } from '../lib/db/savedViews';
import { saveSequence, subscribeSequences } from '../lib/db/outreachSequences';
import {
  enrollContact,
  subscribeEnrollments,
  updateEnrollment,
} from '../lib/db/outreachEnrollments';
import { sequenceStats, dueSteps } from '../lib/crm/sequenceRunner';
import {
  whatGoesOutThisWeek,
  type DueSequenceStepInput,
  type OutboxGroup,
  type PendingEmailApprovalInput,
  type WeeklyUpdateDueInput,
} from '../lib/crm/outboxWeek';
import type { SequenceStep } from '../lib/crm/outreach';
import { uuid } from '../lib/db/store';
import { subscribeApprovals, queueApproval } from '../lib/agent/approvals';
import { applyFilters } from '../lib/crm/viewFilter';
import { mergeTimeline } from '../lib/crm/unifiedTimeline';
import type { ApprovalItem, OutreachEnrollment, OutreachSequence, Person, SavedView, Ticket } from '../lib/db/types';
import {
  buildEnrollmentPlan,
  submitEnrollmentForApproval,
  SUPPRESSION_REASON_LABELS,
  type EnrollmentBatch,
  type EnrollmentContact,
  type EnrollmentPlan,
  type SuppressionContext,
} from '../lib/crm/sequenceEnrollment';
import * as argoWorker from '../lib/agent/argoWorker';
import {
  draftResurrectionSequence,
  selectResurrectionCandidates,
  submitResurrectionForApproval,
  DEFAULT_COOLDOWN_DAYS,
  type LostLeadContext,
  type ResurrectionDeal,
  type ResurrectionDraft,
} from '../lib/crm/lostLeadResurrection';
import type { TimelineEvent } from '../lib/crm/recordTimeline';
import { TICKET_OWNERS } from '../lib/db/tickets';
import {
  computeStepFunnels,
  sequenceHealth,
  variantWinner,
  type EmailLogEvent,
} from '../lib/crm/sequenceAnalytics';
import {
  AUTOMATION_RECIPES,
  RECIPE_AUDIENCE_LABELS,
  buildCampaignSendDiffs,
  filterRecipeAudience,
  recipeToSequenceInput,
  validateCampaign,
  type AutomationRecipe,
  type CampaignBlock,
  type RecipeAudience,
} from '../lib/crm/emailRecipes';
import {
  explainQueuedState,
  nextBestAction,
  parseSendNote,
  summarizeEmailAnalytics,
} from '../lib/crm/emailMarketingAnalytics';
import {
  buildAutoUnenrollReversal,
  classifyReply,
  handleReply,
  restoredEnrollments,
  type AutoUnenrollAction,
  type ClassifiedReply,
  type ReplyAction,
  type ReplyIntent,
  type ReversalPlan,
} from '../lib/crm/sequenceReplyEngine';

// ---------------------------------------------------------------------------
// Real-data suppression context
// ---------------------------------------------------------------------------

/** Note prefix that marks an approval row as a governed sequence send. */
const NOTE_PREFIX = 'sequence-enrollment';

/** Parse `sequence-enrollment <seqId> batch <batchId> …` back out of a note. */
function parseEnrollmentNote(note: string | undefined): { sequenceId: string; batchId: string } | null {
  if (!note) return null;
  const m = note.match(/^sequence-enrollment (\S+) batch (\S+)/);
  return m ? { sequenceId: m[1], batchId: m[2] } : null;
}

/** Parse the full send-diff note, step (+ optional variant) included. */
function parseEnrollmentStep(
  note: string | undefined,
): { sequenceId: string; batchId: string; stepId: string; variant?: string } | null {
  if (!note) return null;
  const m = note.match(/^sequence-enrollment (\S+) batch (\S+) step (\S+)/);
  if (!m) return null;
  const variant = note.match(/ variant (\S+)/)?.[1];
  return {
    sequenceId: m[1],
    batchId: m[2],
    stepId: m[3],
    ...(variant !== undefined ? { variant } : {}),
  };
}

/**
 * Task-router discipline for reply autopilot: route objection/follow-up
 * tasks to the least-loaded HUMAN owner (alpha tie-break) — same honest
 * choice src/lib/agent/taskRouterAgent.ts makes for unowned tickets.
 */
function pickLeastLoadedOwner(tickets: Ticket[]): string | null {
  const humans = TICKET_OWNERS.filter((o) => !o.startsWith('claude:')).slice().sort();
  if (humans.length === 0) return null;
  const load = new Map<string, number>(humans.map((o) => [o, 0]));
  for (const t of tickets) {
    if (!t.owner || t.status === 'done' || t.status === 'cancelled') continue;
    if (load.has(t.owner)) load.set(t.owner, (load.get(t.owner) ?? 0) + 1);
  }
  let best = humans[0];
  for (const o of humans) {
    if ((load.get(o) ?? 0) < (load.get(best) ?? 0)) best = o;
  }
  return best;
}

/** Colour per classified intent — reused by the pills in the Replies list. */
const INTENT_COLOR: Record<ReplyIntent, string> = {
  interested: C.green,
  objection: C.orange,
  ooo: C.textMuted,
  unsubscribe: C.orangeWarm,
  wrong_person: C.orange,
  neutral: C.textDim,
};

/** One classified inbound reply + the autopilot's resulting actions. */
interface ReplyRow {
  id: string;
  contactId: string;
  contactName: string;
  text: string;
  atMs: number;
  sequenceId: string;
  classified: ClassifiedReply;
  actions: ReplyAction[];
  /** Undo plan — the unenroll's own reversal, or a fresh restore plan. */
  plan: ReversalPlan;
}

/** Human label for the governed action a reply triggered. */
function replyActionLabel(a: ReplyAction): string {
  switch (a.kind) {
    case 'auto_unenroll':
      return `Auto-unenrolled from ${a.sequenceIds.length} sequence${a.sequenceIds.length === 1 ? '' : 's'}`;
    case 'pause_enrollment':
      return a.resumeAfter !== undefined
        ? `Paused — resumes ${new Date(a.resumeAfter).toLocaleDateString()}`
        : 'Paused for human review';
    case 'route_task':
      return `Task routed → ${a.owner ? `@${a.owner}` : 'triage'}`;
    case 'queue_rebuttal_draft':
      return 'Rebuttal drafted → approval queue';
  }
}

/** Person → the loose contact shape the enrollment engine consumes. */
function toContact(p: Person): EnrollmentContact {
  return {
    id: p.id,
    name: p.name,
    ...(p.email !== undefined ? { email: p.email } : {}),
    ...(p.title !== undefined ? { title: p.title } : {}),
    accountId: p.company ?? p.id,
    ...(p.company !== undefined ? { accountName: p.company, company: p.company } : {}),
    ...(p.ownerName !== undefined ? { ownerName: p.ownerName } : {}),
    tags: p.tags ?? [],
  };
}

/**
 * Build the suppression context from LIVE collections. Every lens reads
 * real records; a missing signal honestly means "no evidence to suppress".
 */
function buildContext(
  people: Person[],
  tickets: Ticket[],
  approvals: ApprovalItem[],
): SuppressionContext {
  const byId = new Map(people.map((p) => [p.id, p]));
  const activeTickets = tickets.filter((t) => t.status !== 'done');

  return {
    activityFor: (contactId) => {
      const p = byId.get(contactId);
      return (p?.activity ?? []).flatMap((a) => {
        if (a.kind !== 'email') return [];
        if (/\b(replied|responded|wrote back|got a reply)\b/i.test(a.text)) {
          return [{ kind: 'reply', at: a.at, text: a.text }];
        }
        if (/\bbounce/i.test(a.text)) return [{ kind: 'bounce', at: a.at, text: a.text }];
        if (/\bunsubscribe/i.test(a.text)) return [{ kind: 'unsubscribe', at: a.at, text: a.text }];
        return [{ kind: 'email', direction: 'out' as const, at: a.at, text: a.text }];
      });
    },
    hasOpenDeal: (contact) => {
      const p = byId.get(contact.id);
      return p?.category === 'prospect' && (p.stage === 'qualified' || p.stage === 'proposal');
    },
    hasActiveTicket: (contact) => {
      const company = (contact.company ?? contact.accountName ?? '').toLowerCase();
      if (company === '') return false;
      return activeTickets.some(
        (t) =>
          t.title.toLowerCase().includes(company) ||
          t.labels.some((l) => l.toLowerCase() === company),
      );
    },
    // Real CRM status, never an invented score: a churned account is a 100,
    // a paused one 75 (over the default 70 threshold), otherwise unknown.
    churnRiskScore: (contact) => {
      const p = byId.get(contact.id);
      if (p?.status === 'churned') return 100;
      if (p?.status === 'paused') return 75;
      return null;
    },
    timelineFor: (contactId) => {
      const p = byId.get(contactId);
      const notes = (p?.activity ?? []).map((a) => ({
        id: a.id,
        at: a.at,
        text: a.text,
        actor: { kind: a.kind === 'system' ? ('agent' as const) : ('human' as const), id: a.actor ?? 'you' },
      }));
      return mergeTimeline({ notes }, { recordId: contactId });
    },
    activeEnrollments: (contactId) =>
      approvals.flatMap((a) => {
        if (a.status === 'dismissed') return [];
        if (a.action.kind !== 'create_task' || a.action.personId !== contactId) return [];
        const parsed = parseEnrollmentNote(a.note);
        return parsed ? [parsed.sequenceId] : [];
      }),
  };
}

// ---------------------------------------------------------------------------
// Lost-Lead Resurrection — derive the deal book from REAL prospect records
// ---------------------------------------------------------------------------

/**
 * Person roster → the loose deal book the resurrection selector consumes.
 * Every field is read straight off the record — a prospect without a real
 * `closedAt` can never age past the cooldown (we never guess when a deal
 * died), and the timeline is the contact's own logged email/meeting activity.
 */
function toResurrectionDeals(people: Person[]): ResurrectionDeal[] {
  return people
    .filter((p) => p.category === 'prospect' && p.stage !== undefined)
    .map((p) => {
      const closedMs =
        p.stage === 'closed-lost' && p.closedAt ? Date.parse(p.closedAt) : NaN;
      const timeline: TimelineEvent[] = (p.activity ?? []).flatMap((a) => {
        if (a.kind !== 'email' && a.kind !== 'meeting') return [];
        const ts = Date.parse(a.at);
        if (!Number.isFinite(ts)) return [];
        return [
          {
            id: a.id,
            recordId: p.id,
            recordType: 'person' as const,
            source: a.kind,
            ts,
            actor: { kind: 'human' as const, id: a.actor ?? 'you', label: a.actor ?? 'You' },
            summary: a.text,
          },
        ];
      });
      return {
        dealId: p.id,
        accountId: p.company ?? p.id,
        contactId: p.id,
        stage: p.stage as string,
        timeline,
        contactName: p.name,
        ...(p.winLossReason !== undefined ? { lossReason: p.winLossReason } : {}),
        ...(Number.isFinite(closedMs) ? { closedLostAt: closedMs } : {}),
        ...(p.email !== undefined ? { contactEmail: p.email } : {}),
        ...(p.company !== undefined ? { accountName: p.company } : {}),
        ...(p.estimatedValue !== undefined ? { dealValue: p.estimatedValue } : {}),
      };
    });
}

// ---------------------------------------------------------------------------
// Starter sequence (config, not customer data) — user-initiated only
// ---------------------------------------------------------------------------

const STARTER_STEPS = [
  {
    id: 'step-1',
    dayOffset: 0,
    channel: 'email' as const,
    subject: 'Quick intro, {{contact.name}}',
    body: 'Hi {{contact.name}},\n\nReaching out from {{account.owner}}’s team — we work with companies like {{account.name}} on governed AI operations. Worth a 15-minute look?\n\n— {{owner.name}}',
  },
  {
    id: 'step-2',
    dayOffset: 3,
    channel: 'email' as const,
    subject: 'Re: quick intro',
    body: 'Hi {{contact.name}}, floating this back up in case it got buried. Happy to share what a governed rollout looks like for {{account.name}}.',
  },
];

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

type CohortId = 'prospects' | 'customers' | 'lost' | `view:${string}`;

/** Resolve a cohort id against live people + saved views — one honest path. */
function resolveCohort(id: CohortId, people: Person[], views: SavedView[]): Person[] {
  if (id === 'prospects') return filterRecipeAudience('prospects', people);
  if (id === 'customers') return filterRecipeAudience('customers', people);
  if (id === 'lost') return filterRecipeAudience('closed-lost', people);
  const view = views.find((v) => `view:${v.id}` === id);
  if (!view) return [];
  return applyFilters(people, view.filters);
}

/** Recipe audience → the matching built-in cohort in the enrollment picker. */
const AUDIENCE_TO_COHORT: Record<RecipeAudience, CohortId> = {
  prospects: 'prospects',
  customers: 'customers',
  'closed-lost': 'lost',
};

/** Gallery card icons — UI concern, kept out of the pure recipe module. */
const RECIPE_ICONS: Record<string, typeof Mail> = {
  welcome: HeartHandshake,
  're-engage-lost': RefreshCw,
  'renewal-reminder': CalendarClock,
  'weekly-update': Newspaper,
};

/** Note prefix that marks an approval row as a governed campaign send. */
const CAMPAIGN_NOTE_PREFIX = 'campaign-send';

/** Fresh starter blocks for the campaign editor. */
function defaultCampaignBlocks(): CampaignBlock[] {
  return [
    { kind: 'heading', text: 'Hi {{firstName}}' },
    { kind: 'paragraph', text: 'Write your update for {{company}} here.' },
  ];
}


export function EmailMarketingView() {
  // Live collections ---------------------------------------------------------
  const [people, setPeople] = useState<Person[] | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [views, setViews] = useState<SavedView[]>([]);
  const [sequences, setSequences] = useState<OutreachSequence[] | null>(null);
  const [approvals, setApprovals] = useState<ApprovalItem[]>([]);

  // Selections + flow state --------------------------------------------------
  const [cohortId, setCohortId] = useState<CohortId>('prospects');
  const [sequenceId, setSequenceId] = useState<string>('');
  const [plan, setPlan] = useState<EnrollmentPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ batchId: string; itemCount: number } | null>(null);

  // Lost-Lead Resurrection state ----------------------------------------------
  const [resDrafts, setResDrafts] = useState<Record<string, ResurrectionDraft>>({});
  const [resBusy, setResBusy] = useState<string | null>(null);
  const [resSubmitted, setResSubmitted] = useState<Record<string, { batchId: string; itemCount: number }>>({});
  const [resError, setResError] = useState<string | null>(null);

  // Recipe gallery state -------------------------------------------------------
  const [recipeBusy, setRecipeBusy] = useState<string | null>(null);
  const [recipeCreated, setRecipeCreated] = useState<Record<string, string>>({});

  // Campaign block-editor state -------------------------------------------------
  const [campaignSubject, setCampaignSubject] = useState('');
  const [campaignBlocks, setCampaignBlocks] = useState<CampaignBlock[]>(defaultCampaignBlocks);
  const [campaignCohortId, setCampaignCohortId] = useState<CohortId>('customers');
  const [campaignSubmitting, setCampaignSubmitting] = useState(false);
  const [campaignSubmitted, setCampaignSubmitted] = useState<{ id: string; count: number } | null>(null);
  const [campaignError, setCampaignError] = useState<string | null>(null);

  // Reply-handling autopilot + per-step analytics state -----------------------
  const [replyRows, setReplyRows] = useState<ReplyRow[] | null>(null);
  const [undoBusy, setUndoBusy] = useState<string | null>(null);
  const [undone, setUndone] = useState<Record<string, string>>({});
  const [undoError, setUndoError] = useState<string | null>(null);

  // Sequences section (list + steps editor + enroll + enrollment table) -------
  const [enrollments, setEnrollments] = useState<OutreachEnrollment[]>([]);
  const [seqSelectedId, setSeqSelectedId] = useState<string>('');
  const [seqSteps, setSeqSteps] = useState<SequenceStep[]>([]);
  const [seqEnrollPersonId, setSeqEnrollPersonId] = useState<string>('');
  const [seqBusy, setSeqBusy] = useState(false);
  const [seqError, setSeqError] = useState<string | null>(null);

  // Visible feedback + scroll targets so a hero chip / "Use this recipe"
  // lands on the right (now-collapsed) section.
  const toast = useOptionalToast();
  const sequencesSectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void bootstrapPeopleIfEmpty();
    const unsubs = [
      subscribePeople(setPeople),
      subscribeSavedViews(setViews, 'people'),
      subscribeSequences(setSequences),
      subscribeApprovals(setApprovals),
      subscribeEnrollments(setEnrollments),
    ];
    listTickets()
      .then(setTickets)
      .catch(() => setTickets([]));
    return () => unsubs.forEach((u) => u());
  }, []);

  // Default to the first sequence once they load.
  useEffect(() => {
    if (sequenceId === '' && sequences && sequences.length > 0) {
      setSequenceId(sequences[0].id);
    }
  }, [sequences, sequenceId]);

  const sequence = useMemo(
    () => (sequences ?? []).find((s) => s.id === sequenceId) ?? null,
    [sequences, sequenceId],
  );

  // Sequences section: the sequence selected in the LIST (independent of the
  // enrollment-flow `sequenceId` above), and its steps loaded into the editor.
  const seqSelected = useMemo(
    () => (sequences ?? []).find((s) => s.id === seqSelectedId) ?? null,
    [sequences, seqSelectedId],
  );
  useEffect(() => {
    setSeqSteps(seqSelected ? seqSelected.steps.map((s) => ({ ...s })) : []);
    setSeqEnrollPersonId('');
    setSeqError(null);
  }, [seqSelectedId, seqSelected]);

  // Cohort: honest built-ins + real saved views over the people collection.
  const cohort = useMemo<Person[]>(
    () => resolveCohort(cohortId, people ?? [], views),
    [people, views, cohortId],
  );

  // Campaign audience — same CRM-backed resolver, independent selection.
  const campaignAudience = useMemo<Person[]>(
    () => resolveCohort(campaignCohortId, people ?? [], views),
    [people, views, campaignCohortId],
  );
  const campaignEmailable = useMemo(
    () => campaignAudience.filter((p) => (p.email ?? '').trim() !== ''),
    [campaignAudience],
  );
  const campaignIssues = useMemo(
    () => validateCampaign(campaignSubject, campaignBlocks),
    [campaignSubject, campaignBlocks],
  );

  const emailable = useMemo(
    () => cohort.filter((p) => (p.email ?? '').trim() !== ''),
    [cohort],
  );
  const noEmailCount = cohort.length - emailable.length;

  // Pending state for the submitted batch — REAL rows from approvals.
  const batchPending = useMemo(() => {
    if (!submitted) return null;
    const rows = approvals.filter(
      (a) => parseEnrollmentNote(a.note)?.batchId === submitted.batchId,
    );
    return {
      pending: rows.filter((a) => a.status === 'pending').length,
      approved: rows.filter((a) => a.status === 'approved').length,
      dismissed: rows.filter((a) => a.status === 'dismissed').length,
      total: rows.length,
    };
  }, [approvals, submitted]);

  // Resurrectable closed-lost deals — a pure filter over live records.
  const resurrectionCandidates = useMemo<LostLeadContext[]>(
    () =>
      selectResurrectionCandidates(
        toResurrectionDeals(people ?? []),
        Date.now(),
        DEFAULT_COOLDOWN_DAYS,
      ),
    [people],
  );

  // --- Reply autopilot + analytics derivations --------------------------------

  // Active sequence enrollments per contact — REAL rows from approvals.
  const enrollmentsByContact = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const a of approvals) {
      if (a.status === 'dismissed') continue;
      if (a.action.kind !== 'create_task' || !a.action.personId) continue;
      const parsed = parseEnrollmentNote(a.note);
      if (!parsed) continue;
      const list = map.get(a.action.personId) ?? [];
      if (!list.includes(parsed.sequenceId)) list.push(parsed.sequenceId);
      map.set(a.action.personId, list);
    }
    return map;
  }, [approvals]);

  // Inbound replies from ENROLLED contacts — read off the real activity trail.
  const inboundReplies = useMemo(() => {
    const rows: Array<Omit<ReplyRow, 'classified' | 'actions' | 'plan'>> = [];
    for (const p of people ?? []) {
      const enrollments = enrollmentsByContact.get(p.id) ?? [];
      if (enrollments.length === 0) continue;
      for (const act of p.activity ?? []) {
        if (act.kind !== 'email') continue;
        const inbound =
          act.text.trimStart().startsWith('←') ||
          /\b(replied|responded|wrote back|got a reply)\b/i.test(act.text);
        if (!inbound) continue;
        const atMs = Date.parse(act.at);
        rows.push({
          id: `${p.id}:${act.id}`,
          contactId: p.id,
          contactName: p.name,
          text: act.text,
          atMs: Number.isFinite(atMs) ? atMs : Date.now(),
          sequenceId: enrollments[0],
        });
      }
    }
    rows.sort((a, b) => b.atMs - a.atMs);
    return rows;
  }, [people, enrollmentsByContact]);

  // Classify every reply — the REAL worker when configured, deterministic
  // heuristics otherwise — then run the pure autopilot decision.
  useEffect(() => {
    let cancelled = false;
    if (inboundReplies.length === 0) {
      setReplyRows([]);
      return;
    }
    void (async () => {
      const rows: ReplyRow[] = [];
      for (const r of inboundReplies) {
        const classified = await classifyReply(
          {
            contactId: r.contactId,
            sequenceId: r.sequenceId,
            text: r.text,
            at: r.atMs,
            contactName: r.contactName,
          },
          argoWorker,
        );
        const enrollments = enrollmentsByContact.get(r.contactId) ?? [r.sequenceId];
        const actions = handleReply(classified, {
          activeEnrollments: enrollments,
          contactName: r.contactName,
          routeOwner: () => pickLeastLoadedOwner(tickets),
        });
        const unenroll = actions.find(
          (a): a is AutoUnenrollAction => a.kind === 'auto_unenroll',
        );
        const plan =
          unenroll?.reversal ?? buildAutoUnenrollReversal(r.contactId, enrollments);
        rows.push({ ...r, classified, actions, plan });
      }
      if (!cancelled) setReplyRows(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [inboundReplies, enrollmentsByContact, tickets]);

  // Funnel events — approved send diffs are governed sends; classified
  // replies are attributed to the contact's latest sent step. Nothing is
  // fabricated: delivery/open/click stay 0 until a tracking atom emits them.
  const analyticsEvents = useMemo(() => {
    const events: EmailLogEvent[] = [];
    const lastStepByContact = new Map<string, { stepId: string; variant?: string }>();
    for (const a of approvals) {
      const parsed = parseEnrollmentStep(a.note);
      if (!parsed) continue;
      const contactId = a.action.kind === 'create_task' ? a.action.personId : undefined;
      if (a.status === 'approved') {
        events.push({
          kind: 'sent',
          stepId: parsed.stepId,
          ...(parsed.variant !== undefined ? { variant: parsed.variant } : {}),
          ...(contactId !== undefined ? { contactId } : {}),
          sequenceId: parsed.sequenceId,
        });
        if (contactId) {
          lastStepByContact.set(contactId, {
            stepId: parsed.stepId,
            ...(parsed.variant !== undefined ? { variant: parsed.variant } : {}),
          });
        }
      }
    }
    for (const row of replyRows ?? []) {
      const step = lastStepByContact.get(row.contactId);
      if (!step) continue;
      events.push({
        kind: 'replied',
        stepId: step.stepId,
        ...(step.variant !== undefined ? { variant: step.variant } : {}),
        contactId: row.contactId,
        sequenceId: row.sequenceId,
        intent: row.classified.intent,
      });
    }
    return events;
  }, [approvals, replyRows]);

  const funnels = useMemo(() => computeStepFunnels(analyticsEvents), [analyticsEvents]);

  const winner = useMemo(
    () => variantWinner(funnels, 'positiveReplyRate') ?? variantWinner(funnels, 'replyRate'),
    [funnels],
  );
  const health = useMemo(() => sequenceHealth(funnels), [funnels]);

  // Per-sequence analytics — a pure fold over the SAME real approval rows
  // and observed events; names resolve from the saved sequences.
  const sequenceNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of sequences ?? []) map[s.id] = s.name;
    return map;
  }, [sequences]);

  const emailAnalytics = useMemo(
    () => summarizeEmailAnalytics(approvals, analyticsEvents, sequenceNames),
    [approvals, analyticsEvents, sequenceNames],
  );

  // Live queue state for a just-queued campaign — REAL rows from approvals.
  const campaignPending = useMemo(() => {
    if (!campaignSubmitted) return null;
    const rows = approvals.filter((a) => parseSendNote(a.note)?.id === campaignSubmitted.id);
    return {
      pending: rows.filter((a) => a.status === 'pending').length,
      approved: rows.filter((a) => a.status === 'approved').length,
      dismissed: rows.filter((a) => a.status === 'dismissed').length,
    };
  }, [approvals, campaignSubmitted]);

  // The single most useful next step — a deterministic priority ladder.
  const nextAction = useMemo(
    () =>
      nextBestAction({
        sequences: (sequences ?? []).length,
        cohortEmailable: emailable.length,
        planEnrolled: plan && !submitted ? plan.enrolled.length : null,
        queued: emailAnalytics.totals.queued,
        sent: emailAnalytics.totals.sent,
        healthWarnings: health.warnings.length,
      }),
    [sequences, emailable.length, plan, submitted, emailAnalytics, health.warnings.length],
  );

  // --- This week's outbox — the hero summary ---------------------------------

  // What should actually go out this week, folded from three REAL, already
  // governed sources: the pure due-step engine, pending weekly-update
  // proposals, and pending email send diffs in the approvals inbox. Pure
  // fold — the view just renders it.
  const outboxWeek = useMemo(() => {
    const now = Date.now();
    const personName = new Map((people ?? []).map((p) => [p.id, p.name]));

    // 1 · Sequence emails due — dueSteps over every sequence's enrollments.
    const dueSequenceSteps: DueSequenceStepInput[] = [];
    for (const seq of sequences ?? []) {
      for (const due of dueSteps(seq, enrollments, now)) {
        dueSequenceSteps.push({
          sequenceId: seq.id,
          sequenceName: seq.name,
          recipientName:
            due.enrollment.personName ??
            personName.get(due.enrollment.personId) ??
            due.enrollment.personId,
        });
      }
    }

    // 2 · Weekly customer updates due — pending send_customer_update proposals.
    const weeklyUpdatesDue: WeeklyUpdateDueInput[] = approvals
      .filter((a) => a.status === 'pending' && a.action.kind === 'send_customer_update')
      .map((a) => {
        const act = a.action as { personId: string; personName?: string };
        return {
          personId: act.personId,
          recipientName:
            act.personName ?? personName.get(act.personId) ?? act.personId,
        };
      });

    // 3 · Drafts awaiting approval — pending governed email send diffs
    // (sequence enrollments + one-off campaigns), excluding weekly updates
    // which get their own group above.
    const pendingEmailApprovals: PendingEmailApprovalInput[] = approvals
      .filter((a) => {
        if (a.status !== 'pending') return false;
        if (a.action.kind === 'send_customer_update') return false;
        const isEnrollment = parseEnrollmentNote(a.note) !== null;
        const isCampaign = parseSendNote(a.note) !== null;
        const isDraftedEmail = a.action.kind === 'send_drafted_email';
        return isEnrollment || isCampaign || isDraftedEmail;
      })
      .map((a) => {
        const act = a.action as { personName?: string; personId?: string };
        return {
          id: a.id,
          recipientName:
            act.personName ??
            (act.personId ? personName.get(act.personId) : undefined) ??
            'a recipient',
        };
      });

    return whatGoesOutThisWeek({
      dueSequenceSteps,
      weeklyUpdatesDue,
      pendingEmailApprovals,
    });
  }, [sequences, enrollments, approvals, people]);

  /** Route a hero action chip to the right (collapsed) section or the inbox. */
  const handleOutboxAction = (group: OutboxGroup) => {
    const { target } = group.action;
    if (target === 'sequences') {
      sequencesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (target === 'approvals') {
      // The approvals inbox lives on the Dashboard; navigate there.
      window.location.hash = '#/dashboard';
    }
  };

  // --- Actions ----------------------------------------------------------------

  /** Undo an autopilot decision: queue the compiled restore proposal —
   *  a reversal is just another governed proposal, never a direct write. */
  const handleUndoReply = async (row: ReplyRow) => {
    if (undoBusy !== null || undone[row.id]) return;
    setUndoBusy(row.id);
    setUndoError(null);
    try {
      const seqs = restoredEnrollments(row.plan);
      await queueApproval(
        {
          kind: 'create_task',
          title: `Restore ${seqs.length} sequence enrollment${seqs.length === 1 ? '' : 's'} for ${row.contactName}`,
          personId: row.contactId,
          personName: row.contactName,
        },
        'autopilot',
        row.plan.restore.note,
      );
      setUndone((prev) => ({ ...prev, [row.id]: row.plan.restore.id }));
    } catch (e) {
      setUndoError(e instanceof Error ? e.message : 'Queuing the restore failed.');
    } finally {
      setUndoBusy(null);
    }
  };

  const handleDraftWinback = async (ctx: LostLeadContext) => {
    if (resBusy) return;
    setResBusy(ctx.dealId);
    setResError(null);
    try {
      // The REAL worker when configured; the module degrades to the
      // deterministic drafter on error/timeout/absence — never breaks.
      const draft = await draftResurrectionSequence(ctx, argoWorker);
      setResDrafts((prev) => ({ ...prev, [ctx.dealId]: draft }));
    } catch (e) {
      setResError(e instanceof Error ? e.message : 'Drafting the win-back failed.');
    } finally {
      setResBusy(null);
    }
  };

  const handleApproveWinback = async (ctx: LostLeadContext) => {
    const draft = resDrafts[ctx.dealId];
    if (!draft || resBusy !== null || resSubmitted[ctx.dealId]) return;
    setResBusy(ctx.dealId);
    setResError(null);
    try {
      // The pure engine emits ONE vetoed batch via sequenceEnrollment…
      let batch: EnrollmentBatch | null = null;
      const result = submitResurrectionForApproval(draft, ctx, {
        push: (b) => void (batch = b),
      });
      const captured = batch as EnrollmentBatch | null;
      if (!captured) throw new Error('No batch produced');
      // …persisted as governed diffs in the REAL approvals collection.
      for (const item of captured.items) {
        await queueApproval(
          {
            kind: 'create_task',
            title: `Win-back "${item.subject}" to ${item.recipient || ctx.contactName || ctx.contactId} (step ${item.stepId})`,
            personId: item.contactId,
            ...(ctx.contactName ? { personName: ctx.contactName } : {}),
            ...(ctx.accountName ? { accountName: ctx.accountName } : {}),
          },
          'autopilot',
          `${NOTE_PREFIX} ${item.sequenceId} batch ${captured.batchId} step ${item.stepId} mailbox ${item.mailbox} — ${item.diff}`,
        );
      }
      setResSubmitted((prev) => ({ ...prev, [ctx.dealId]: result }));
    } catch (e) {
      setResError(e instanceof Error ? e.message : 'Submitting the win-back failed.');
    } finally {
      setResBusy(null);
    }
  };

  const handlePreview = () => {
    setError(null);
    setSubmitted(null);
    if (!sequence) {
      setError('Pick a sequence first — a plan needs a cadence to enroll into.');
      return;
    }
    const ctx = buildContext(people ?? [], tickets, approvals);
    setPlan(buildEnrollmentPlan(emailable.map(toContact), sequence, ctx));
  };

  const handleApprove = async () => {
    if (!plan || !sequence || plan.enrolled.length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // The pure engine emits ONE vetoed batch; this adapter captures it…
      let batch: EnrollmentBatch | null = null;
      const result = submitEnrollmentForApproval(
        plan,
        { push: (b) => void (batch = b) },
        { id: sequence.id, name: sequence.name, steps: sequence.steps },
      );
      // …then persists every send diff into the REAL approvals collection,
      // tagged with the batch id so pending state and future dedupe
      // (already_enrolled) read straight off the store.
      const captured = batch as EnrollmentBatch | null;
      if (!captured) throw new Error('No batch produced');
      const byId = new Map((people ?? []).map((p) => [p.id, p]));
      for (const item of captured.items) {
        const person = byId.get(item.contactId);
        await queueApproval(
          {
            kind: 'create_task',
            title: `Send "${item.subject || sequence.name}" to ${item.recipient} (step ${item.stepId})`,
            personId: item.contactId,
            ...(person?.name ? { personName: person.name } : {}),
            ...(person?.company ? { accountName: person.company } : {}),
          },
          'autopilot',
          `${NOTE_PREFIX} ${item.sequenceId} batch ${captured.batchId} step ${item.stepId} mailbox ${item.mailbox} — ${item.diff}`,
        );
      }
      setSubmitted(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submitting the enrollment failed.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Recipe card → a REAL, editable sequence via the existing engine. The
   * pure mapping produces `saveSequence` input; we then OPEN it in the
   * Sequences section (select it + scroll there) and toast what happened and
   * what's next. If a sequence with that name already exists we open the
   * existing one — no duplicates — and say so. Enrollment still flows through
   * blast-radius preview + the vetoed approval batch; nothing auto-sends.
   */
  const handleUseRecipe = async (recipe: AutomationRecipe) => {
    if (recipeBusy !== null) return;
    setRecipeBusy(recipe.id);
    setError(null);
    setSeqError(null);
    try {
      const input = recipeToSequenceInput(recipe);
      // Dedupe by name against what's persisted right now (case-insensitive).
      const existing = findSequenceByName(sequences ?? [], input.name);
      const seq = existing ?? (await saveSequence(input));

      setRecipeCreated((prev) => ({ ...prev, [recipe.id]: seq.id }));
      // Open it in the Sequences section — the visible, editable landing.
      setSeqSelectedId(seq.id);
      // Also prime the enrollment flow with the recipe's default audience.
      setSequenceId(seq.id);
      setCohortId(AUDIENCE_TO_COHORT[recipe.audience]);
      setPlan(null);
      setSubmitted(null);

      // Scroll the section into view so the operator sees the result land.
      // Guarded for jsdom, where scrollIntoView is not implemented.
      requestAnimationFrame(() => {
        const el = sequencesSectionRef.current;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      if (existing) {
        toast?.push({
          kind: 'info',
          text: `Opened "${seq.name}" — it already exists.`,
          detail: 'Review the steps, then enroll contacts.',
        });
      } else {
        toast?.push({
          kind: 'success',
          text: `Created "${seq.name}" — review the steps, then enroll contacts.`,
        });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not create the recipe sequence.';
      setError(msg);
      setSeqError(msg);
    } finally {
      setRecipeBusy(null);
    }
  };

  // --- Campaign block editor -----------------------------------------------

  const addBlock = (block: CampaignBlock) => {
    setCampaignBlocks((prev) => [...prev, block]);
    setCampaignSubmitted(null);
  };

  const updateBlock = (index: number, block: CampaignBlock) => {
    setCampaignBlocks((prev) => prev.map((b, i) => (i === index ? block : b)));
    setCampaignSubmitted(null);
  };

  const removeBlock = (index: number) => {
    setCampaignBlocks((prev) => prev.filter((_, i) => i !== index));
    setCampaignSubmitted(null);
  };

  /**
   * Compile the campaign into per-contact send diffs (pure) and queue
   * every one as a governed approval — the same ledgered path sequence
   * enrollments take. Nothing sends without a human approving each diff.
   */
  const handleQueueCampaign = async () => {
    if (campaignSubmitting || campaignIssues.length > 0) return;
    const diffs = buildCampaignSendDiffs(
      campaignSubject,
      campaignBlocks,
      campaignEmailable.map((p) => ({
        id: p.id,
        name: p.name,
        ...(p.email !== undefined ? { email: p.email } : {}),
        ...(p.company !== undefined ? { company: p.company } : {}),
      })),
    );
    if (diffs.length === 0) {
      setCampaignError('No one in this audience has an email address — nothing to queue.');
      return;
    }
    setCampaignSubmitting(true);
    setCampaignError(null);
    try {
      const campaignId = `campaign-${Date.now().toString(36)}`;
      const byId = new Map((people ?? []).map((p) => [p.id, p]));
      for (const d of diffs) {
        const person = byId.get(d.contactId);
        await queueApproval(
          {
            kind: 'create_task',
            title: `Campaign "${d.subject}" to ${d.recipient}`,
            personId: d.contactId,
            ...(person?.name ? { personName: person.name } : {}),
            ...(person?.company ? { accountName: person.company } : {}),
          },
          'autopilot',
          `${CAMPAIGN_NOTE_PREFIX} ${campaignId} to ${d.recipient} — subject: ${d.subject} — ${d.text}`,
        );
      }
      setCampaignSubmitted({ id: campaignId, count: diffs.length });
    } catch (e) {
      setCampaignError(e instanceof Error ? e.message : 'Queuing the campaign failed.');
    } finally {
      setCampaignSubmitting(false);
    }
  };

  const handleStarterSequence = async () => {
    setError(null);
    try {
      const seq = await saveSequence({ name: 'Intro cadence (starter)', steps: STARTER_STEPS });
      setSequenceId(seq.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the starter sequence.');
    }
  };

  // --- Sequences section handlers --------------------------------------------

  const handleAddSeqStep = () => {
    setSeqSteps((prev) => [
      ...prev,
      {
        id: uuid(),
        dayOffset: prev.length === 0 ? 0 : (prev[prev.length - 1].dayOffset ?? 0) + 3,
        channel: 'email',
        subject: '',
        body: '',
      },
    ]);
  };

  const handleRemoveSeqStep = (id: string) => {
    setSeqSteps((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSeqStepChange = (id: string, patch: Partial<SequenceStep>) => {
    setSeqSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const handleSaveSeqSteps = async () => {
    if (!seqSelected) return;
    setSeqBusy(true);
    setSeqError(null);
    try {
      await saveSequence({ id: seqSelected.id, name: seqSelected.name, steps: seqSteps });
    } catch (e) {
      setSeqError(e instanceof Error ? e.message : 'Could not save the sequence steps.');
    } finally {
      setSeqBusy(false);
    }
  };

  const handleEnrollPerson = async () => {
    if (!seqSelected || seqEnrollPersonId === '') return;
    const person = (people ?? []).find((p) => p.id === seqEnrollPersonId);
    setSeqBusy(true);
    setSeqError(null);
    try {
      await enrollContact({
        sequenceId: seqSelected.id,
        personId: seqEnrollPersonId,
        ...(person?.name !== undefined ? { personName: person.name } : {}),
      });
      setSeqEnrollPersonId('');
    } catch (e) {
      setSeqError(e instanceof Error ? e.message : 'Could not enroll the contact.');
    } finally {
      setSeqBusy(false);
    }
  };

  const handleMarkReplied = async (enrollmentId: string) => {
    setSeqError(null);
    try {
      await updateEnrollment(enrollmentId, { status: 'stopped', stoppedReason: 'replied' });
    } catch (e) {
      setSeqError(e instanceof Error ? e.message : 'Could not stop the enrollment.');
    }
  };

  // --- Render ------------------------------------------------------------------

  const loading = people === null || sequences === null;

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 980, margin: '0 auto' }}>
      <GlassPanel style={{ padding: '18px 20px', display: 'grid', gap: 6 }}>
        <div className="t-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.text }}>
          <Mail size={18} style={{ color: C.magentaSoft }} /> Email Marketing
        </div>
        <div className="t-meta" style={{ color: C.textMuted }}>
          Start from a recipe or build a one-off campaign. Every send is deduped, suppressed against
          live CRM signals, previewed, and queued as approvable diffs — nothing sends without a human.
        </div>
      </GlassPanel>

      {/* This week's outbox — the hero: what should actually go out, folded
          from the due-step engine, weekly updates due, and pending approvals.
          The machinery below explains HOW; this band says WHAT and NOW. */}
      <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 10 }}>
        <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Send size={16} style={{ color: C.magentaSoft }} /> This week's outbox
          {!outboxWeek.isEmpty && (
            <span className="t-meta" style={{ color: C.textMuted }}>· {outboxWeek.total} to go out</span>
          )}
        </span>
        {outboxWeek.isEmpty ? (
          <div className="t-meta" style={{ color: C.textMuted }}>
            Nothing due this week — enroll contacts in a sequence or turn on the weekly customer update.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            {outboxWeek.groups.map((g) => (
              <div key={g.key} className="liquid-glass liquid-glass--default" style={{ padding: '12px 14px', display: 'grid', gap: 6, borderRadius: 14 }}>
                <div style={{ color: C.text, fontWeight: 600 }}>{g.title}</div>
                <div className="t-meta" style={{ color: C.textMuted }}>
                  {g.previewNames.join(', ')}
                  {g.moreCount > 0 ? ` +${g.moreCount} more` : ''}
                </div>
                <div>
                  <span
                    className="glass-btn"
                    role="button"
                    tabIndex={0}
                    style={{ padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                    onClick={() => handleOutboxAction(g)}
                  >
                    {g.action.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>

      {/* How it works — the four honest stages of the governed send path */}
      <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
        <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Workflow size={16} style={{ color: C.magentaSoft }} /> How it works
        </span>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          {(
            [
              {
                icon: Sparkles,
                title: '1 · Recipe → sequence',
                body:
                  'Pick a recipe (or the campaign builder for a one-off). One click creates a ' +
                  'governed sequence — nothing is sent at this point.',
              },
              {
                icon: ShieldCheck,
                title: '2 · Preview the blast radius',
                body:
                  'Live CRM signals suppress risky contacts — replies, bounces, unsubscribes, open ' +
                  'deals, active tickets — with a per-contact reason for every hold-out.',
              },
              {
                icon: Inbox,
                title: '3 · Queue for approval',
                body:
                  'Each recipient becomes ONE send diff in the Approvals inbox on the Dashboard. ' +
                  '"Queued" means waiting for a human there — nothing sends on its own.',
              },
              {
                icon: Send,
                title: '4 · Approve → ledgered send',
                body:
                  'Approving a diff executes its governed send task and seals it in the ledger. ' +
                  'Delivery rides the same outbox → Brevo Cloud Function transactional mail uses; ' +
                  'until the mail atom auto-works send tasks, an approved diff is a task on your ' +
                  'board — never a silent blast.',
              },
            ] as const
          ).map((step) => (
            <div key={step.title} style={{ display: 'grid', gap: 6, alignContent: 'start' }}>
              <div className="t-meta" style={{ color: C.text, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                <step.icon size={14} style={{ color: C.magentaSoft }} /> {step.title}
              </div>
              <div className="t-meta" style={{ color: C.textMuted }}>{step.body}</div>
            </div>
          ))}
        </div>
        <div className="t-meta" style={{ color: C.textDim }}>
          Opens/clicks only appear once Brevo actually reports them via a webhook — until then the
          analytics below show “—”, never a fabricated number.
        </div>
      </GlassPanel>

      {loading ? (
        <GlassPanel style={{ padding: 32, display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
          <Loader2 size={16} className="animate-spin" style={{ color: C.textMuted }} />
          <span className="t-meta" style={{ color: C.textMuted }}>Loading cohorts, sequences and approvals…</span>
        </GlassPanel>
      ) : (
        <>
          {/* Next best action — one deterministic smart prompt, never a guess */}
          <GlassPanel style={{ padding: '14px 18px', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <span className="glass-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: `${C.magentaSoft}1e`, color: C.magentaSoft, flexShrink: 0 }}>
              <Lightbulb size={12} /> Next best action
            </span>
            <div style={{ display: 'grid', gap: 2, flex: '1 1 320px' }}>
              <span className="t-meta" style={{ color: C.text, fontWeight: 600 }}>{nextAction.label}</span>
              <span className="t-meta" style={{ color: C.textMuted }}>{nextAction.detail}</span>
            </div>
          </GlassPanel>

          {/* Automation recipe gallery — the landing lead */}
          <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={16} style={{ color: C.magentaSoft }} /> Start with a recipe
              </span>
              <span className="t-meta" style={{ color: C.textDim }}>
                One click creates a governed sequence — enrollment still goes through preview + approval.
              </span>
            </div>
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              {AUTOMATION_RECIPES.map((recipe) => {
                const Icon = RECIPE_ICONS[recipe.id] ?? Mail;
                const audienceSize = filterRecipeAudience(
                  recipe.audience,
                  people ?? [],
                ).length;
                const created = recipeCreated[recipe.id] !== undefined;
                const busy = recipeBusy === recipe.id;
                const lastDay = recipe.steps[recipe.steps.length - 1]?.dayOffset ?? 0;
                return (
                  <GlassPanel key={recipe.id} variant="subtle" style={{ padding: '14px 14px', display: 'grid', gap: 8, alignContent: 'start' }}>
                    <div className="t-meta" style={{ color: C.text, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                      <Icon size={16} style={{ color: C.magentaSoft }} /> {recipe.name}
                    </div>
                    <div className="t-meta" style={{ color: C.textMuted }}>{recipe.tagline}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      <span className="glass-pill" style={{ fontSize: 10 }}>
                        {recipe.steps.length} email{recipe.steps.length === 1 ? '' : 's'} / {lastDay === 0 ? 'same day' : `${lastDay} days`}
                      </span>
                      <span className="glass-pill" style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Users size={10} /> {RECIPE_AUDIENCE_LABELS[recipe.audience]} · {audienceSize}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={created ? 'glass-btn' : 'glass-btn glass-btn--primary'}
                      onClick={() => void handleUseRecipe(recipe)}
                      disabled={recipeBusy !== null}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
                    >
                      {busy ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : created ? (
                        <CheckCircle2 size={13} style={{ color: C.green }} />
                      ) : (
                        <Sparkles size={13} />
                      )}
                      {busy ? 'Creating sequence…' : created ? 'Sequence ready — re-select' : 'Use this recipe'}
                    </button>
                  </GlassPanel>
                );
              })}
            </div>
          </GlassPanel>

          {/* Campaign block editor — one-off governed blast */}
          <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <LayoutTemplate size={16} style={{ color: C.magentaSoft }} /> Campaign builder
              </span>
              <span className="t-meta" style={{ color: C.textDim }}>
                Subject + blocks, audience from CRM — queued as governed send diffs, never a direct send.
              </span>
            </div>

            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              {/* Editor column */}
              <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span className="t-meta" style={{ color: C.textDim }}>Subject (tokens: {'{{firstName}} {{name}} {{company}}'})</span>
                  <input
                    className="glass-input"
                    value={campaignSubject}
                    placeholder="e.g. Update for {{company}}"
                    onChange={(e) => {
                      setCampaignSubject(e.target.value);
                      setCampaignSubmitted(null);
                    }}
                  />
                </label>

                {campaignBlocks.map((b, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <span className="glass-pill" style={{ fontSize: 10, marginTop: 6, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {b.kind === 'heading' && <Type size={10} />}
                      {b.kind === 'paragraph' && <AlignLeft size={10} />}
                      {b.kind === 'button' && <MousePointerClick size={10} />}
                      {b.kind === 'divider' && <Minus size={10} />}
                      {b.kind}
                    </span>
                    <div style={{ flex: 1, display: 'grid', gap: 4 }}>
                      {b.kind === 'heading' && (
                        <input
                          className="glass-input"
                          value={b.text}
                          onChange={(e) => updateBlock(i, { kind: 'heading', text: e.target.value })}
                        />
                      )}
                      {b.kind === 'paragraph' && (
                        <textarea
                          className="glass-input"
                          rows={3}
                          value={b.text}
                          onChange={(e) => updateBlock(i, { kind: 'paragraph', text: e.target.value })}
                        />
                      )}
                      {b.kind === 'button' && (
                        <>
                          <input
                            className="glass-input"
                            placeholder="Button label"
                            value={b.label}
                            onChange={(e) => updateBlock(i, { ...b, label: e.target.value })}
                          />
                          <input
                            className="glass-input"
                            placeholder="https://…"
                            value={b.url}
                            onChange={(e) => updateBlock(i, { ...b, url: e.target.value })}
                          />
                        </>
                      )}
                      {b.kind === 'divider' && (
                        <div style={{ borderTop: `1px solid ${C.textDim}44`, marginTop: 12 }} />
                      )}
                    </div>
                    <button
                      type="button"
                      className="glass-btn"
                      aria-label={`Remove ${b.kind} block`}
                      onClick={() => removeBlock(i)}
                      style={{ padding: '4px 8px', flexShrink: 0 }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  <button type="button" className="glass-btn" onClick={() => addBlock({ kind: 'heading', text: '' })} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Type size={12} /> Heading
                  </button>
                  <button type="button" className="glass-btn" onClick={() => addBlock({ kind: 'paragraph', text: '' })} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <AlignLeft size={12} /> Paragraph
                  </button>
                  <button type="button" className="glass-btn" onClick={() => addBlock({ kind: 'button', label: '', url: '' })} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <MousePointerClick size={12} /> Button
                  </button>
                  <button type="button" className="glass-btn" onClick={() => addBlock({ kind: 'divider' })} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Minus size={12} /> Divider
                  </button>
                </div>
              </div>

              {/* Audience + queue column */}
              <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span className="t-meta" style={{ color: C.textDim }}>Audience (from CRM)</span>
                  <select
                    className="glass-input"
                    value={campaignCohortId}
                    onChange={(e) => {
                      setCampaignCohortId(e.target.value as CohortId);
                      setCampaignSubmitted(null);
                    }}
                  >
                    <option value="prospects">All prospects</option>
                    <option value="customers">All customers</option>
                    <option value="lost">Closed-lost prospects</option>
                    {views.map((v) => (
                      <option key={v.id} value={`view:${v.id}`}>
                        Saved view: {v.name}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="t-meta" style={{ color: C.textDim }}>
                  {campaignAudience.length} in audience · {campaignEmailable.length} with an email address
                  {campaignAudience.length - campaignEmailable.length > 0
                    ? ` · ${campaignAudience.length - campaignEmailable.length} excluded (no email — never guessed)`
                    : ''}
                </div>

                {campaignIssues.length > 0 && (
                  <div style={{ display: 'grid', gap: 3 }}>
                    {campaignIssues.map((issue) => (
                      <div key={issue} className="t-meta" style={{ color: C.orange, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <AlertTriangle size={12} /> {issue}
                      </div>
                    ))}
                  </div>
                )}
                {campaignError && (
                  <div className="t-meta" style={{ color: C.orangeWarm, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertTriangle size={12} /> {campaignError}
                  </div>
                )}

                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={() => void handleQueueCampaign()}
                  disabled={campaignSubmitting || campaignIssues.length > 0 || campaignEmailable.length === 0 || campaignSubmitted !== null}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
                >
                  {campaignSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {campaignSubmitting
                    ? 'Queuing campaign…'
                    : `Queue campaign (${campaignEmailable.length} send diff${campaignEmailable.length === 1 ? '' : 's'})`}
                </button>
                <span className="t-meta" style={{ color: C.textDim }}>
                  Each recipient becomes one approvable diff in the approvals inbox — the same ledgered
                  path sequence sends take.
                </span>

                {campaignSubmitted && (
                  <GlassPanel variant="subtle" style={{ padding: '12px 14px', display: 'grid', gap: 4 }}>
                    <div className="t-meta" style={{ color: C.green, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CheckCircle2 size={13} /> Campaign queued — {campaignSubmitted.count} send diff
                      {campaignSubmitted.count === 1 ? '' : 's'} pending approval
                    </div>
                    <div className="t-meta" style={{ color: C.textMuted }}>
                      Campaign id: <code style={{ fontSize: 11 }}>{campaignSubmitted.id}</code>
                    </div>
                    {campaignPending && (
                      <div className="t-meta" style={{ color: C.textMuted, display: 'flex', gap: 6 }}>
                        <Inbox size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>{explainQueuedState(campaignPending)}</span>
                      </div>
                    )}
                  </GlassPanel>
                )}
              </div>
            </div>
          </GlassPanel>

          {/* Pickers */}
          <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
            <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={16} style={{ color: C.magentaSoft }} /> Governed enrollment
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              <label style={{ display: 'grid', gap: 4, flex: '1 1 220px' }}>
                <span className="t-meta" style={{ color: C.textDim }}>Cohort (saved views + built-ins)</span>
                <select
                  className="glass-input"
                  value={cohortId}
                  onChange={(e) => {
                    setCohortId(e.target.value as CohortId);
                    setPlan(null);
                    setSubmitted(null);
                  }}
                >
                  <option value="prospects">All prospects</option>
                  <option value="customers">All customers</option>
                  <option value="lost">Closed-lost prospects</option>
                  {views.map((v) => (
                    <option key={v.id} value={`view:${v.id}`}>
                      Saved view: {v.name}
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 4, flex: '1 1 220px' }}>
                <span className="t-meta" style={{ color: C.textDim }}>Sequence</span>
                {sequences.length === 0 ? (
                  <button type="button" className="glass-btn" onClick={handleStarterSequence}>
                    No sequences yet — create a starter cadence
                  </button>
                ) : (
                  <select
                    className="glass-input"
                    value={sequenceId}
                    onChange={(e) => {
                      setSequenceId(e.target.value);
                      setPlan(null);
                      setSubmitted(null);
                    }}
                  >
                    {sequences.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} · {s.steps.length} step{s.steps.length === 1 ? '' : 's'}
                      </option>
                    ))}
                  </select>
                )}
              </label>

              <button
                type="button"
                className="glass-btn"
                onClick={handlePreview}
                disabled={!sequence || emailable.length === 0}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Radar size={14} /> Preview blast radius
              </button>
            </div>

            <div className="t-meta" style={{ color: C.textDim }}>
              {cohort.length} in cohort · {emailable.length} with an email address
              {noEmailCount > 0 ? ` · ${noEmailCount} excluded (no email — never guessed)` : ''}
            </div>
          </GlassPanel>

          {error && (
            <GlassPanel style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertTriangle size={14} style={{ color: C.orangeWarm, flexShrink: 0 }} />
              <span className="t-meta" style={{ color: C.orangeWarm }}>{error}</span>
            </GlassPanel>
          )}

          {/* Empty state before a preview */}
          {!plan && !error && (
            <GlassPanel style={{ padding: 24 }}>
              <div className="t-meta" style={{ color: C.textMuted }}>
                Pick a cohort and a sequence, then preview the blast radius. You’ll see exactly who
                enrolls, who is held out and WHY, before anything is submitted for approval.
              </div>
            </GlassPanel>
          )}

          {/* The plan: blast radius + enrolled/suppressed split */}
          {plan && (
            <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 14 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span className="t-section-title" style={{ color: C.text }}>Blast radius</span>
                <span className="glass-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Users size={12} /> {plan.blastRadius.contacts} contacts
                </span>
                <span className="glass-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Building2 size={12} /> {plan.blastRadius.accounts} accounts
                </span>
                <span className="glass-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Globe size={12} /> {plan.blastRadius.domains} domains
                </span>
                <span className="glass-pill" style={{ background: `${C.orange}22`, color: C.orange }}>
                  {plan.suppressed.length} suppressed
                </span>
              </div>

              <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                {/* Enrolled */}
                <div style={{ display: 'grid', gap: 6, alignContent: 'start' }}>
                  <div className="t-meta" style={{ color: C.green, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ShieldCheck size={13} /> Enrolled ({plan.enrolled.length})
                  </div>
                  {plan.enrolled.length === 0 && (
                    <div className="t-meta" style={{ color: C.textDim }}>
                      No one clears suppression — the whole cohort is protected.
                    </div>
                  )}
                  {plan.enrolled.map((c) => (
                    <div key={c.contactId} className="t-meta" style={{ color: C.textMuted }}>
                      {c.email || c.contactId}
                      <span style={{ color: C.textDim }}> · {c.accountId}</span>
                    </div>
                  ))}
                </div>

                {/* Suppressed with reasons */}
                <div style={{ display: 'grid', gap: 6, alignContent: 'start' }}>
                  <div className="t-meta" style={{ color: C.orange, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ShieldOff size={13} /> Suppressed ({plan.suppressed.length})
                  </div>
                  {plan.suppressed.length === 0 && (
                    <div className="t-meta" style={{ color: C.textDim }}>No suppressions — clean cohort.</div>
                  )}
                  {plan.suppressed.map((d) => (
                    <div key={d.contactId} style={{ display: 'grid', gap: 3 }}>
                      <div className="t-meta" style={{ color: C.textMuted, display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                        {d.contactId}
                        {d.reasons.map((r) => (
                          <span
                            key={r}
                            className="glass-pill"
                            style={{ fontSize: 10, padding: '2px 8px', background: `${C.orange}1e`, color: C.orange }}
                          >
                            {SUPPRESSION_REASON_LABELS[r]}
                          </span>
                        ))}
                      </div>
                      <div className="t-meta" style={{ color: C.textDim, fontSize: 11 }}>
                        {d.provenance.reasons.join(' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={() => void handleApprove()}
                  disabled={plan.enrolled.length === 0 || submitting || submitted !== null}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {submitting
                    ? 'Submitting batch…'
                    : `Approve enrollment (${plan.enrolled.length} send diff${plan.enrolled.length === 1 ? '' : 's'})`}
                </button>
                <span className="t-meta" style={{ color: C.textDim }}>
                  Submits ONE vetoed batch to the approval queue — nothing executes until a human approves each diff.
                </span>
              </div>

              {submitted && (
                <GlassPanel variant="subtle" style={{ padding: '12px 14px', display: 'grid', gap: 4 }}>
                  <div className="t-meta" style={{ color: C.green, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle2 size={13} /> Batch submitted — {submitted.itemCount} send diff
                    {submitted.itemCount === 1 ? '' : 's'} pending approval
                  </div>
                  <div className="t-meta" style={{ color: C.textMuted }}>
                    Batch id: <code style={{ fontSize: 11 }}>{submitted.batchId}</code>
                  </div>
                  {batchPending && (
                    <>
                      <div className="t-meta" style={{ color: C.textDim }}>
                        Live queue state: {batchPending.pending} pending · {batchPending.approved} approved ·{' '}
                        {batchPending.dismissed} dismissed · {batchPending.total} total.
                      </div>
                      <div className="t-meta" style={{ color: C.textMuted, display: 'flex', gap: 6 }}>
                        <Inbox size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>{explainQueuedState(batchPending)}</span>
                      </div>
                    </>
                  )}
                </GlassPanel>
              )}
            </GlassPanel>
          )}

          {/* Lost-Lead Resurrection — the agentic win-back drafter */}
          <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={16} style={{ color: C.magentaSoft }} /> Lost-Lead Resurrection
              </span>
              <span className="glass-pill">
                {resurrectionCandidates.length} candidate{resurrectionCandidates.length === 1 ? '' : 's'}
              </span>
              <span className="glass-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Cpu size={11} />
                {argoWorker.isConfigured() ? 'Claude worker live' : 'No worker configured — deterministic drafts'}
              </span>
            </div>
            <div className="t-meta" style={{ color: C.textMuted }}>
              Closed-lost deals quiet for {DEFAULT_COOLDOWN_DAYS}+ days — no reply since the loss, no new open
              deal on the account. Each draft is reasoned from the captured loss reason + record timeline and
              stakes a 30-day reopen contract. Approving routes send diffs into the approval queue; nothing
              auto-sends.
            </div>

            {resError && (
              <div className="t-meta" style={{ color: C.orangeWarm, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={13} /> {resError}
              </div>
            )}

            {resurrectionCandidates.length === 0 ? (
              <div className="t-meta" style={{ color: C.textDim }}>
                No resurrectable deals right now — every closed-lost deal is inside its cooldown, already
                re-engaged, or on an account with a live deal in flight.
              </div>
            ) : (
              resurrectionCandidates.map((ctx) => {
                const draft = resDrafts[ctx.dealId];
                const done = resSubmitted[ctx.dealId];
                const busy = resBusy === ctx.dealId;
                return (
                  <GlassPanel key={ctx.dealId} variant="subtle" style={{ padding: '12px 14px', display: 'grid', gap: 10 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      <span className="t-meta" style={{ color: C.text }}>
                        {ctx.contactName ?? ctx.contactId}
                        {ctx.accountName ? <span style={{ color: C.textDim }}> · {ctx.accountName}</span> : null}
                      </span>
                      <span className="glass-pill" style={{ fontSize: 10, background: `${C.orange}1e`, color: C.orange }}>
                        Lost: {ctx.lossReason}
                      </span>
                      <span className="glass-pill" style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={10} /> closed {new Date(ctx.closedLostAt).toLocaleDateString()}
                      </span>
                      {typeof ctx.dealValue === 'number' && ctx.dealValue > 0 && (
                        <span className="glass-pill" style={{ fontSize: 10 }}>
                          ${ctx.dealValue.toLocaleString('en-US')} lost pipeline
                        </span>
                      )}
                    </div>

                    {!draft ? (
                      <div>
                        <button
                          type="button"
                          className="glass-btn"
                          onClick={() => void handleDraftWinback(ctx)}
                          disabled={resBusy !== null}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                          {busy ? 'Drafting win-back…' : 'Draft win-back sequence'}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          <span
                            className="glass-pill"
                            style={{
                              fontSize: 10,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              background: draft.source === 'worker' ? `${C.green}22` : `${C.orange}22`,
                              color: draft.source === 'worker' ? C.green : C.orange,
                            }}
                          >
                            <Cpu size={10} /> {draft.source === 'worker' ? 'Claude worker' : 'Deterministic fallback'}
                          </span>
                          {draft.grounded && (
                            <span className="glass-pill" style={{ fontSize: 10, background: `${C.green}1e`, color: C.green }}>
                              Grounded in loss reason
                            </span>
                          )}
                        </div>

                        {draft.steps.map((s, i) => (
                          <div key={`${ctx.dealId}-step-${i}`} style={{ display: 'grid', gap: 2 }}>
                            <div className="t-meta" style={{ color: C.text }}>
                              Step {i + 1} · day {s.delayDays} · {s.subject}
                              <span className="glass-pill" style={{ marginLeft: 6, fontSize: 10 }}>
                                score {s.contentScore}
                              </span>
                            </div>
                            <div className="t-meta" style={{ color: C.textDim, whiteSpace: 'pre-wrap', fontSize: 11 }}>
                              {s.body}
                            </div>
                          </div>
                        ))}

                        <div className="t-meta" style={{ color: C.textMuted, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <FileCheck2 size={12} />
                          Outcome contract: reopen conversation ({draft.outcomeContract.predicate.kind}) by{' '}
                          {new Date(draft.outcomeContract.predicate.window.end).toLocaleDateString()} ·{' '}
                          ${draft.outcomeContract.dollarsAtStake.toLocaleString('en-US')} at stake
                        </div>

                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="glass-btn glass-btn--primary"
                            onClick={() => void handleApproveWinback(ctx)}
                            disabled={resBusy !== null || done !== undefined}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                            {busy ? 'Submitting…' : `Approve win-back (${draft.steps.length} send diffs)`}
                          </button>
                          {done ? (
                            <span className="t-meta" style={{ color: C.green, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              <CheckCircle2 size={12} /> Submitted — {done.itemCount} diff
                              {done.itemCount === 1 ? '' : 's'} pending in batch{' '}
                              <code style={{ fontSize: 10 }}>{done.batchId}</code>
                            </span>
                          ) : (
                            <span className="t-meta" style={{ color: C.textDim }}>
                              Routes into the approval queue — a human approves every send.
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </GlassPanel>
                );
              })
            )}
          </GlassPanel>

          {/* Per-sequence send analytics — queued vs sent vs vetoed, honest opens */}
          <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <BarChart3 size={16} style={{ color: C.magentaSoft }} /> Email analytics
              </span>
              <span className="glass-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Inbox size={11} /> {emailAnalytics.totals.queued} queued
              </span>
              <span
                className="glass-pill"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, ...(emailAnalytics.totals.sent > 0 ? { background: `${C.green}1e`, color: C.green } : {}) }}
              >
                <Send size={11} /> {emailAnalytics.totals.sent} sent
              </span>
              <span className="glass-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Reply size={11} /> {emailAnalytics.totals.replies} repl{emailAnalytics.totals.replies === 1 ? 'y' : 'ies'}
              </span>
            </div>
            <div className="t-meta" style={{ color: C.textMuted }}>
              Every row is a real count off the approvals collection: queued = diffs waiting for a
              human in the Approvals inbox, sent = diffs a human approved (governed send executed,
              ledgered), vetoed = diffs a human dismissed. Opens show “—” until a Brevo webhook
              reports real open events — never a fabricated zero.
            </div>
            {emailAnalytics.perSequence.length === 0 ? (
              <div className="t-meta" style={{ color: C.textDim }}>
                No send diffs recorded yet — queue a campaign or submit an enrollment preview above
                and this table fills in from the real approvals collection.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 4 }}>
                <div
                  className="t-meta"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.6fr 0.8fr repeat(6, 0.7fr)',
                    gap: 6,
                    color: C.textDim,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  <span>Sequence</span>
                  <span>Type</span>
                  <span>Queued</span>
                  <span>Sent</span>
                  <span>Vetoed</span>
                  <span>Batches</span>
                  <span>Replies</span>
                  <span>Opens</span>
                </div>
                {emailAnalytics.perSequence.map((r) => (
                  <div
                    key={r.id}
                    className="t-meta"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.6fr 0.8fr repeat(6, 0.7fr)',
                      gap: 6,
                      color: C.textMuted,
                    }}
                  >
                    <span style={{ color: C.text }}>{r.name}</span>
                    <span>{r.kind === 'campaign' ? 'campaign' : 'sequence'}</span>
                    <span style={{ color: r.queued > 0 ? C.orange : undefined }}>{r.queued}</span>
                    <span style={{ color: r.sent > 0 ? C.green : undefined }}>{r.sent}</span>
                    <span>{r.vetoed}</span>
                    <span>{r.batches}</span>
                    <span style={{ color: r.replies > 0 ? C.green : undefined }}>{r.replies}</span>
                    <span>{emailAnalytics.openTrackingAvailable ? r.opens : '—'}</span>
                  </div>
                ))}
              </div>
            )}
            {emailAnalytics.totals.queued > 0 && (
              <div className="t-meta" style={{ color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Inbox size={12} />
                {explainQueuedState({
                  pending: emailAnalytics.totals.queued,
                  approved: emailAnalytics.totals.sent,
                  dismissed: emailAnalytics.totals.vetoed,
                })}
              </div>
            )}
          </GlassPanel>

          {/* Per-step / per-variant sequence analytics */}
          <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <BarChart3 size={16} style={{ color: C.magentaSoft }} /> Sequence Performance
              </span>
              {winner && (
                <span
                  className="glass-pill"
                  style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4, background: `${C.green}1e`, color: C.green }}
                >
                  <Trophy size={10} /> {winner.stepId} · variant {winner.winner} wins (+{Math.round(winner.lift * 100)}% lift, reply-led)
                </span>
              )}
              {health.warnings.map((w) => (
                <span
                  key={w}
                  className="glass-pill"
                  style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4, background: `${C.orange}1e`, color: C.orange }}
                >
                  <AlertTriangle size={10} /> {w}
                </span>
              ))}
            </div>
            <div className="t-meta" style={{ color: C.textMuted }}>
              A pure fold over real email-log events: approved send diffs count as governed sends, classified
              replies attribute to the contact's latest step. Variant winners are decided on REPLY rates only —
              opens are MPP-inflated and never crown a winner. Delivery/open/click stay 0 until a tracking atom
              emits them; nothing here is fabricated.
            </div>
            {funnels.length === 0 ? (
              <div className="t-meta" style={{ color: C.textDim }}>
                No send events yet — approve an enrollment batch and the funnel fills in from real approvals.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 4 }}>
                <div
                  className="t-meta"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr 0.8fr repeat(8, 0.7fr)',
                    gap: 6,
                    color: C.textDim,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  <span>Step</span>
                  <span>Variant</span>
                  <span>Sent</span>
                  <span>Deliv.</span>
                  <span>Opened</span>
                  <span>Clicked</span>
                  <span>Replied</span>
                  <span>Positive</span>
                  <span>Meetings</span>
                  <span>Bounced</span>
                </div>
                {funnels.map((f) => (
                  <div
                    key={`${f.stepId}|${f.variant ?? ''}`}
                    className="t-meta"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.4fr 0.8fr repeat(8, 0.7fr)',
                      gap: 6,
                      color: C.textMuted,
                    }}
                  >
                    <span style={{ color: C.text }}>{f.stepId}</span>
                    <span>
                      {f.variant ?? '—'}
                      {winner && winner.winner === f.variant && winner.stepId === f.stepId && (
                        <Trophy size={10} style={{ color: C.green, marginLeft: 4, verticalAlign: 'middle' }} />
                      )}
                    </span>
                    <span>{f.sent}</span>
                    <span>{f.delivered}</span>
                    <span>{f.opened}</span>
                    <span>{f.clicked}</span>
                    <span style={{ color: f.replied > 0 ? C.green : undefined }}>{f.replied}</span>
                    <span style={{ color: f.positiveReplied > 0 ? C.green : undefined }}>{f.positiveReplied}</span>
                    <span>{f.meetingBooked}</span>
                    <span style={{ color: f.bounced > 0 ? C.orange : undefined }}>{f.bounced}</span>
                  </div>
                ))}
              </div>
            )}
          </GlassPanel>

          {/* Replies — the reply-handling autopilot */}
          <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Reply size={16} style={{ color: C.magentaSoft }} /> Replies — autopilot
              </span>
              <span className="glass-pill">
                {(replyRows ?? []).length} repl{(replyRows ?? []).length === 1 ? 'y' : 'ies'}
              </span>
              <span className="glass-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Cpu size={11} />
                {argoWorker.isConfigured() ? 'Claude classifier live' : 'No worker — heuristic classifier'}
              </span>
            </div>
            <div className="t-meta" style={{ color: C.textMuted }}>
              A reply always stops the cadence — the #1 lead-burning bug is a sequence that keeps firing.
              Positive replies and unsubscribes auto-unenroll everywhere as a REVERSIBLE ledgered action,
              OOO pauses with a scheduled resume, objections route a task plus a playbook-grounded rebuttal
              draft into the approval queue. Every decision has an Undo.
            </div>

            {undoError && (
              <div className="t-meta" style={{ color: C.orangeWarm, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={13} /> {undoError}
              </div>
            )}

            {replyRows === null ? (
              <div className="t-meta" style={{ color: C.textMuted, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Loader2 size={14} className="animate-spin" /> Classifying replies…
              </div>
            ) : replyRows.length === 0 ? (
              <div className="t-meta" style={{ color: C.textDim }}>
                No inbound replies from enrolled contacts yet — sync Gmail on a contact or approve an
                enrollment batch first.
              </div>
            ) : (
              replyRows.map((row) => {
                const busy = undoBusy === row.id;
                const isUndone = undone[row.id] !== undefined;
                const color = INTENT_COLOR[row.classified.intent];
                return (
                  <GlassPanel key={row.id} variant="subtle" style={{ padding: '12px 14px', display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      <span className="t-meta" style={{ color: C.text }}>
                        {row.contactName}
                        <span style={{ color: C.textDim }}> · {new Date(row.atMs).toLocaleDateString()}</span>
                      </span>
                      <span className="glass-pill" style={{ fontSize: 10, background: `${color}1e`, color }}>
                        {row.classified.intent.replace('_', ' ')} · {Math.round(row.classified.confidence * 100)}%
                      </span>
                      <span className="glass-pill" style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Cpu size={10} /> {row.classified.source === 'worker' ? 'Claude worker' : 'Deterministic fallback'}
                      </span>
                    </div>
                    <div className="t-meta" style={{ color: C.textDim, fontSize: 11 }}>{row.text}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      {row.actions.map((a, i) => (
                        <span key={`${row.id}-a-${i}`} className="glass-pill" style={{ fontSize: 10 }}>
                          {replyActionLabel(a)}
                        </span>
                      ))}
                      <button
                        type="button"
                        className="glass-btn"
                        onClick={() => void handleUndoReply(row)}
                        disabled={busy || isUndone || undoBusy !== null}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '4px 10px' }}
                      >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
                        {busy ? 'Queuing restore…' : 'Undo'}
                      </button>
                      {isUndone && (
                        <span className="t-meta" style={{ color: C.green, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <CheckCircle2 size={11} /> Restore queued for approval (
                          <code style={{ fontSize: 10 }}>{undone[row.id]}</code>)
                        </span>
                      )}
                    </div>
                  </GlassPanel>
                );
              })
            )}
          </GlassPanel>

          {/* How email sending actually works — honest explainer, links to Integrations */}
          <GlassPanel variant="subtle" animate={false} style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <Mail size={16} style={{ color: C.blueLight, flexShrink: 0, marginTop: 2 }} />
              <div>
                <h3
                  className="t-section-title"
                  style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  How email sending works
                </h3>
                <p className="t-meta" style={{ margin: '4px 0 0' }}>
                  Argo doesn&apos;t blast mail from your browser. When a send is approved, the app
                  queues it to the Firestore <code>outbox</code> collection and a background Cloud
                  Function delivers it through Brevo — so there&apos;s no public send endpoint and it
                  works under strict org sharing policies. Transactional sending is built in today;
                  inbound/replies capture and custom sending domains are still coming. See{' '}
                  <strong>Admin → Integrations</strong> for the live status of email delivery and
                  every other connection.
                </p>
              </div>
            </div>
          </GlassPanel>

          {/* Sequences — list + steps editor + enroll + enrollment table */}
          <div ref={sequencesSectionRef} data-testid="sequences-section">
          <GlassPanel style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span className="t-section-title" style={{ color: C.text, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Workflow size={16} style={{ color: C.magentaSoft }} /> Sequences
              </span>
              <span className="glass-pill">
                {(sequences ?? []).length} sequence{(sequences ?? []).length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="t-meta" style={{ color: C.textMuted }}>
              Edit a cadence's steps, enroll contacts, and track where each one sits. A reply marks
              the enrollment stopped so the cadence never fires at someone who wrote back.
            </div>

            {seqError && (
              <div className="t-meta" style={{ color: C.orangeWarm, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={13} /> {seqError}
              </div>
            )}

            {(sequences ?? []).length === 0 ? (
              <div className="t-meta" style={{ color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Minus size={13} /> No sequences yet — create one above to start enrolling contacts.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                {/* Left: sequence list with derived enrollment chips */}
                <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
                  {(sequences ?? []).map((s) => {
                    const stats = sequenceStats(s, enrollments);
                    const selected = s.id === seqSelectedId;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className="glass-panel"
                        onClick={() => setSeqSelectedId(s.id)}
                        style={{
                          textAlign: 'left',
                          padding: '10px 12px',
                          display: 'grid',
                          gap: 6,
                          cursor: 'pointer',
                          border: `1px solid ${selected ? C.magentaSoft : `${C.textDim}44`}`,
                          background: selected ? `${C.magentaSoft}14` : 'transparent',
                        }}
                      >
                        <span className="t-meta" style={{ color: C.text, fontWeight: 600 }}>{s.name}</span>
                        <span className="t-meta" style={{ color: C.textMuted }}>
                          {s.steps.length} step{s.steps.length === 1 ? '' : 's'}
                        </span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {stats.enrolled === 0 ? (
                            <span className="glass-pill" style={{ fontSize: 10, color: C.textDim }}>— not enrolled</span>
                          ) : (
                            <>
                              <span className="glass-pill" style={{ fontSize: 10 }}>
                                {stats.enrolled} enrolled
                              </span>
                              <span className="glass-pill" style={{ fontSize: 10, background: `${C.green}1e`, color: C.green }}>
                                {stats.active} active
                              </span>
                              <span className="glass-pill" style={{ fontSize: 10, background: `${C.magentaSoft}1e`, color: C.magentaSoft }}>
                                {stats.completed} completed
                              </span>
                            </>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Right: detail — steps editor, enroll picker, enrollment table */}
                <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
                  {!seqSelected ? (
                    <div className="t-meta" style={{ color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Minus size={13} /> Select a sequence to edit its steps and enrollments.
                    </div>
                  ) : (
                    <>
                      <div className="t-meta" style={{ color: C.text, fontWeight: 600 }}>{seqSelected.name}</div>

                      {/* Zero-enrollment nudge — the honest "what's next". */}
                      {sequenceStats(seqSelected, enrollments).enrolled === 0 && (
                        <div
                          className="t-meta"
                          style={{ color: C.magentaSoft, display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <Lightbulb size={13} /> Next: enroll contacts — sends queue for your
                          approval, nothing auto-sends.
                        </div>
                      )}

                      {/* Steps editor */}
                      <div style={{ display: 'grid', gap: 8 }}>
                        {seqSteps.length === 0 ? (
                          <div className="t-meta" style={{ color: C.textDim }}>No steps — add one below.</div>
                        ) : (
                          seqSteps.map((step, i) => (
                            <div
                              key={step.id}
                              style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}
                            >
                              <label style={{ display: 'grid', gap: 2 }}>
                                <span className="t-meta" style={{ color: C.textDim, fontSize: 10 }}>Day</span>
                                <input
                                  aria-label={`Step ${i + 1} day offset`}
                                  type="number"
                                  min={0}
                                  value={step.dayOffset}
                                  onChange={(e) =>
                                    handleSeqStepChange(step.id, { dayOffset: Number(e.target.value) || 0 })
                                  }
                                  style={{ width: 60 }}
                                />
                              </label>
                              <div style={{ display: 'grid', gap: 4, flex: '1 1 200px' }}>
                                <input
                                  aria-label={`Step ${i + 1} subject`}
                                  placeholder="Subject"
                                  value={step.subject ?? ''}
                                  onChange={(e) => handleSeqStepChange(step.id, { subject: e.target.value })}
                                />
                                <textarea
                                  aria-label={`Step ${i + 1} body`}
                                  placeholder="Body"
                                  rows={2}
                                  value={step.body ?? ''}
                                  onChange={(e) => handleSeqStepChange(step.id, { body: e.target.value })}
                                />
                              </div>
                              <button
                                type="button"
                                className="glass-pill"
                                aria-label={`Remove step ${i + 1}`}
                                onClick={() => handleRemoveSeqStep(step.id)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))
                        )}
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <button type="button" className="glass-pill" onClick={handleAddSeqStep} style={{ cursor: 'pointer' }}>
                            + Add step
                          </button>
                          <button
                            type="button"
                            className="glass-pill"
                            onClick={() => void handleSaveSeqSteps()}
                            disabled={seqBusy}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: seqBusy ? 'default' : 'pointer' }}
                          >
                            {seqBusy ? <Loader2 size={12} className="spin" /> : <Send size={12} />} Save steps
                          </button>
                        </div>
                      </div>

                      {/* Enroll picker */}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select
                          aria-label="Enroll a contact"
                          value={seqEnrollPersonId}
                          onChange={(e) => setSeqEnrollPersonId(e.target.value)}
                        >
                          <option value="">Select a contact…</option>
                          {(people ?? []).map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="glass-pill"
                          onClick={() => void handleEnrollPerson()}
                          disabled={seqBusy || seqEnrollPersonId === ''}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            cursor: seqBusy || seqEnrollPersonId === '' ? 'default' : 'pointer',
                          }}
                        >
                          <Users size={12} /> Enroll
                        </button>
                      </div>

                      {/* Enrollment table */}
                      {(() => {
                        const rows = enrollments.filter((e) => e.sequenceId === seqSelected.id);
                        if (rows.length === 0) {
                          return (
                            <div className="t-meta" style={{ color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Minus size={13} /> No one enrolled yet.
                            </div>
                          );
                        }
                        return (
                          <div style={{ display: 'grid', gap: 6 }}>
                            {rows.map((e) => (
                              <div
                                key={e.id}
                                style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
                              >
                                <span className="t-meta" style={{ color: C.text, flex: '1 1 140px' }}>
                                  {e.personName ?? e.personId}
                                </span>
                                <span className="glass-pill" style={{ fontSize: 10 }}>
                                  Step {e.currentStep + 1}
                                </span>
                                <span
                                  className="glass-pill"
                                  style={{
                                    fontSize: 10,
                                    background:
                                      e.status === 'active'
                                        ? `${C.green}1e`
                                        : e.status === 'completed'
                                          ? `${C.magentaSoft}1e`
                                          : `${C.orange}1e`,
                                    color:
                                      e.status === 'active'
                                        ? C.green
                                        : e.status === 'completed'
                                          ? C.magentaSoft
                                          : C.orange,
                                  }}
                                >
                                  {e.status}
                                  {e.stoppedReason ? ` · ${e.stoppedReason}` : ''}
                                </span>
                                {e.status === 'active' && (
                                  <button
                                    type="button"
                                    className="glass-pill"
                                    onClick={() => void handleMarkReplied(e.id)}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
                                  >
                                    <Reply size={12} /> Mark replied
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            )}
          </GlassPanel>
          </div>
        </>
      )}
    </div>
  );
}
